import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DOCUMENT_ENDPOINTS, EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import { DocumentCategory, DocumentSubcategory, DocumentType, DocumentField } from '../types/Document';
import { UserDetails } from '../types/User';
import { DocumentTemplate } from '../types/EducationHub';

// Manages structural metadata (categories, subcategories, types, fields) and provides filtering helpers.

export const useDocumentMap = (authUser: UserDetails | null) => {
	const [categories, setCategories] = useState<DocumentCategory[]>([]);
	const [subcategories, setSubcategories] = useState<DocumentSubcategory[]>([]);
	const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
	const [documentFields, setDocumentFields] = useState<DocumentField[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchAgentMapData = useCallback(async () => {
		const userId = authUser?.id;
		const agencyId = authUser?.agency?.agencyId;

		if (!userId || !agencyId || !authUser?.agency?.isPaymentValid) {
			setCategories([]);
			setSubcategories([]);
			setDocumentTypes([]);
			setDocumentFields([]);
			setLoading(false);
			return;
		}
		
		setLoading(true);
		setError(null);
		try {
			const headers = {
				Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
			};
			
			// Fetch structural data and templates in parallel
			const [mapResponse, templatesResponse] = await Promise.all([
				axios.get(DOCUMENT_ENDPOINTS.AGENT_MAP, { headers }),
				axios.get(EDUCATION_ENDPOINTS.TEMPLATES, { 
					headers,
					params: { userId } // Get all templates
				}).catch(() => ({ data: { templates: [] } })) // Gracefully handle if templates endpoint fails
			]);
			
			const { categories, subcategories, documentTypes, fieldTypes } = mapResponse.data;
			const templates = templatesResponse.data.templates || [];
			
			// Create a map of templates by document type ID
			const templateMap = new Map<string, DocumentTemplate>();
			templates.forEach((template: DocumentTemplate) => {
				// Skip templates without a valid documentTypeId
				if (!template.documentTypeId) return;
				
				templateMap.set(template.documentTypeId, template);
			});
			
			// Map templates to document types
			const documentTypesWithTemplates = (documentTypes || []).map((type: DocumentType) => ({
				...type,
				template: templateMap.get(type.id) || null,
				required: type.required,
				recurrence: type.recurrence || 'none',
				canExpire: (type.recurrence || 'none') !== 'none',
			}));

			// Normalize field data to ensure consistent field properties
			const normalizedFields = (fieldTypes || []).map((f: any) => ({
				...f,
				name: f.name,
				required: f.required,
			}));
			// Update state with the consolidated response
			setCategories(categories || []);
			setSubcategories(subcategories || []);
			setDocumentTypes(documentTypesWithTemplates);
			setDocumentFields(normalizedFields);
		} catch (err: any) {
			setError('Failed to fetch structural data');
			setCategories([]);
			setSubcategories([]);
			setDocumentTypes([]);
			setDocumentFields([]);
		} finally {
			setLoading(false);
		}
	}, [authUser?.id, authUser?.agency?.agencyId, authUser?.agency?.isPaymentValid]);

	useEffect(() => {
		fetchAgentMapData();
	}, [fetchAgentMapData]);

	// Helper methods for filtering data
	const getSubcategoriesForCategory = useCallback((categoryId: string): DocumentSubcategory[] => {
		return subcategories.filter(sub => sub.category_id === categoryId);
	}, [subcategories]);
	
	const getDocumentTypesForSubcategory = useCallback((subcategoryId: string): DocumentType[] => {
		return documentTypes.filter(type => type.subcategory_id === subcategoryId);
	}, [documentTypes]);
	
	const getDocumentTypesForCategory = useCallback((categoryId: string): DocumentType[] => {
		// Get direct category items (no subcategory)
		return documentTypes.filter(type => 
			type.category_id === categoryId && !type.subcategory_id
		);
	}, [documentTypes]);
	
	const getFieldsForSubcategory = useCallback((subcategoryId: string): DocumentField[] => {
		return documentFields.filter(field => field.subcategory_id === subcategoryId);
	}, [documentFields]);
	
	const getFieldsForCategory = useCallback((categoryId: string): DocumentField[] => {
		// Get direct category items (no subcategory)
		return documentFields.filter(field => 
			field.category_id === categoryId && !field.subcategory_id
		);
	}, [documentFields]);
	
	const getCategoriesForUser = (userDetails: UserDetails): DocumentCategory[] => {
		return categories;
	};

	return {
		categories,
		subcategories,
		documentTypes,
		documentFields,
		loading,
		error,
		getSubcategoriesForCategory,
		getDocumentTypesForSubcategory,
		getDocumentTypesForCategory,
		getFieldsForSubcategory,
		getFieldsForCategory,
		getCategoriesForUser,
		refetch: fetchAgentMapData,
	};
};
